import React from "react";
import { Globe, Instagram as InstagramIcon, Youtube, Facebook, Twitter, Linkedin } from "lucide-react";
import { TikTok } from "@/components/icons/TikTok";
import { useCitationStore } from "@/stores/citationStore";
import { MessageCitation } from "@/types/chat";
import { urlsMatch, normalizeUrl } from "@/lib/urlNormalize";

type CanonicalRule = {
  pattern: RegExp;
  canonicalHost: string;
  iconKey?: string;
  hidePath?: boolean;
};

const HOST_CANONICAL_RULES: CanonicalRule[] = [
  { pattern: /(^|\.)cdninstagram\.com$/i, canonicalHost: "instagram.com", iconKey: "instagram.com", hidePath: true },
  { pattern: /instagram\.[\w-]+\.fbcdn\.net$/i, canonicalHost: "instagram.com", iconKey: "instagram.com", hidePath: true },
  { pattern: /(^|\.)fbcdn\.net$/i, canonicalHost: "facebook.com", iconKey: "facebook.com", hidePath: true },
  { pattern: /(^|\.)tiktokcdn\.com$/i, canonicalHost: "tiktok.com", iconKey: "tiktok.com", hidePath: true },
  { pattern: /(^|\.)twimg\.com$/i, canonicalHost: "twitter.com", iconKey: "twitter.com", hidePath: true },
  { pattern: /azurecontainerapps\.io$/i, canonicalHost: "lema-app.com", iconKey: "lema-app.com", hidePath: true },
  { pattern: /azureedge\.net$/i, canonicalHost: "lema-app.com", iconKey: "lema-app.com", hidePath: true },
  { pattern: /edgekey\.net$/i, canonicalHost: "lema-app.com", iconKey: "lema-app.com", hidePath: true },
  { pattern: /edgesuite\.net$/i, canonicalHost: "lema-app.com", iconKey: "lema-app.com", hidePath: true },
];

const PATH_CANONICAL_RULES: CanonicalRule[] = [
  { pattern: /^\/tunnel\b/i, canonicalHost: "lema-app.com", iconKey: "lema-app.com", hidePath: true },
];

const DOMAIN_ICON_MAP: Record<string, () => React.ReactNode> = {
  "tiktok.com": () => <TikTok className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
  "instagram.com": () => <InstagramIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
  "youtube.com": () => <Youtube className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
  "facebook.com": () => <Facebook className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
  "twitter.com": () => <Twitter className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
  "x.com": () => <Twitter className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
  "linkedin.com": () => <Linkedin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
};

const baseClassName =
  "inline-flex h-7 items-center gap-1 rounded-full border border-transparent bg-neutral-200 px-2 text-xs font-medium leading-tight text-neutral-700 transition-colors hover:bg-neutral-300 hover:text-highlight-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:bg-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-600 max-w-full min-w-0";

const TRUNCATE_LIMIT = 60;

const truncateMiddle = (value: string, maxLength: number) => {
  if (value.length <= maxLength) {
    return value;
  }
  const ellipsis = "…";
  const keep = maxLength - ellipsis.length;
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${value.slice(0, head)}${ellipsis}${value.slice(value.length - tail)}`;
};

interface LinkPillProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  citations?: MessageCitation[];
  onCitationClick?: (citation: MessageCitation) => void;
  onCitationHover?: (citation: MessageCitation) => void;
}

export const LinkPill: React.FC<LinkPillProps> = ({
  href,
  children,
  className,
  citations,
  onCitationClick,
  onCitationHover,
  ...props
}) => {
  const { setActiveUrl } = useCitationStore();
  const hrefValue = typeof href === "string" ? href : "";

  // Find matching citation by URL (using normalized comparison)
  const matchingCitation = React.useMemo(() => {
    if (!hrefValue || !citations || citations.length === 0) return null;
    return citations.find(citation => urlsMatch(citation.url, hrefValue)) || null;
  }, [citations, hrefValue]);

  // Use citation URL for matching if found, otherwise use original href
  // Normalize the URL before storing to ensure consistent matching
  const urlForMatching = React.useMemo(() => {
    const urlToUse = matchingCitation?.url || hrefValue;
    return normalizeUrl(urlToUse);
  }, [matchingCitation?.url, hrefValue]);

  const [faviconError, setFaviconError] = React.useState(false);
  const parsedUrl = React.useMemo(() => {
    if (!hrefValue) {
      return null;
    }

    try {
      return new URL(hrefValue);
    } catch {
      return null;
    }
  }, [hrefValue]);

  if (!hrefValue) {
    return <>{children}</>;
  }

  if (!parsedUrl) {
    return null;
  }
  const url = parsedUrl;

  const childText = React.Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join("")
    .trim();

  const originalDomain = url.hostname.replace(/^www\./i, "");

  let canonicalHost = originalDomain;
  let hidePath = false;
  let iconHint: string | null = null;

  const hostRule = HOST_CANONICAL_RULES.find((rule) => rule.pattern.test(url.hostname));
  if (hostRule) {
    canonicalHost = hostRule.canonicalHost;
    hidePath = hostRule.hidePath ?? hidePath;
    iconHint = hostRule.iconKey ?? hostRule.canonicalHost;
  }

  const pathRule = PATH_CANONICAL_RULES.find((rule) => rule.pattern.test(url.pathname));
  if (pathRule) {
    canonicalHost = pathRule.canonicalHost;
    hidePath = pathRule.hidePath ?? hidePath;
    iconHint = pathRule.iconKey ?? iconHint ?? pathRule.canonicalHost;
  }

  const canonicalDomain = canonicalHost.replace(/^www\./i, "");
  const apexDomain = canonicalDomain.split(".").slice(-2).join(".") || canonicalDomain;

  const decodePath = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const rawPath = url.pathname;
  const normalizedPath = rawPath && rawPath !== "/" ? rawPath : "";
  const decodedPath = decodePath(normalizedPath);
  const displayedPath = hidePath ? "" : decodedPath;
  const combinedLabel = displayedPath ? `${canonicalDomain}${displayedPath}` : canonicalDomain;
  const truncatedLabel = truncateMiddle(combinedLabel, TRUNCATE_LIMIT);

  const shouldUseChildren =
    !!childText &&
    childText.toLowerCase() !== href.toLowerCase() &&
    childText.toLowerCase() !== originalDomain.toLowerCase() &&
    childText.toLowerCase() !== canonicalDomain.toLowerCase();

  const rawLabel = shouldUseChildren ? childText : truncatedLabel;
  const displayLabel = truncateMiddle(rawLabel, TRUNCATE_LIMIT);

  const iconLookupOrder = [iconHint, canonicalDomain, apexDomain].filter(Boolean) as string[];
  const iconKey = iconLookupOrder.find((key) => DOMAIN_ICON_MAP[key.toLowerCase()])?.toLowerCase() ?? null;

  let iconElement: React.ReactNode;
  if (iconKey) {
    iconElement = DOMAIN_ICON_MAP[iconKey]?.();
  } else if (!faviconError) {
    const faviconDomain = canonicalDomain || originalDomain;
    const faviconSrc = `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(faviconDomain)}`;
    iconElement = (
      <img
        src={faviconSrc}
        alt=""
        aria-hidden="true"
        className="block h-3.5 w-3.5 shrink-0 rounded-sm"
        onError={() => setFaviconError(true)}
      />
    );
  } else {
    iconElement = <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseClassName}${className ? ` ${className}` : ""}`}
      aria-label={`Open link to ${combinedLabel}`}
      title={hrefValue}
      onMouseEnter={() => {
        setActiveUrl(urlForMatching);
        if (matchingCitation && onCitationHover) {
          onCitationHover(matchingCitation);
        }
      }}
      onMouseLeave={() => setActiveUrl(null)}
      onClick={(e) => {
        // Only prevent default navigation on desktop (lg: 1024px and up)
        // On mobile, let the link navigate normally
        if (window.innerWidth >= 1024) {
          e.preventDefault();
        }

        // If there's a matching citation and onCitationClick handler, trigger it
        if (matchingCitation && onCitationClick) {
          onCitationClick(matchingCitation);
        }
      }}
      {...props}
    >
      {iconElement}
      <span className="min-w-0 truncate max-w-full sm:max-w-[18rem]" dir="auto">
        {displayLabel || canonicalDomain}
      </span>
    </a>
  );
};
